/* eslint-disable no-case-declarations */
import { useCuration } from '@hooks/useCuration';
import { useFeatureFlags } from '@hooks/useFeatureFlags';

import type { DiagnosticReportRecord } from '@models/diagnostic-report';
import type { EncounterRecord } from '@models/encounter';
import type { ObservationRecord } from '@models/observation';

export function useRecordFlags() {
  const { flags } = useFeatureFlags();
  const {
    state: { record },
  } = useCuration();

  let canCreate: boolean | undefined;
  let canEdit: boolean | undefined;
  let canDelete: boolean | undefined;

  switch (record?.resourceType) {
    case 'AllergyIntolerance':
      canCreate = flags.isAllergyIntoleranceCreateAllergyEnabled;
      canEdit = flags.isAllergyIntoleranceUpdateAllergyEnabled;
      canDelete = flags.isAllergyIntoleranceDeleteAllergyEnabled;
      break;
    case 'Appointment':
      canCreate = flags.isAppointmentCreateAppointmentEnabled;
      canEdit = flags.isAppointmentUpdateAppointmentEnabled;
      canDelete = flags.isAppointmentDeleteAppointmentEnabled;
      break;
    case 'Condition':
      canCreate = flags.isConditionCreateConditionEnabled;
      canEdit = flags.isConditionUpdateConditionEnabled;
      canDelete = flags.isConditionDeleteConditionEnabled;
      break;
    case 'Coverage':
      canCreate = flags.isCoverageCreateCoverageEnabled;
      canEdit = flags.isCoverageUpdateCoverageEnabled;
      canDelete = flags.isCoverageDeleteCoverageEnabled;
      break;
    case 'FamilyMemberHistory':
      canCreate = flags.isFamilyMemberHistoryCreateFamilyMemberHistoryEnabled;
      canEdit = flags.isFamilyMemberHistoryUpdateFamilyMemberHistoryEnabled;
      canDelete = flags.isFamilyMemberHistoryDeleteFamilyMemberHistoryEnabled;
      break;
    case 'DiagnosticReport':
      const diagnosticReportRecord = record as DiagnosticReportRecord;
      const diagnosticCategory = diagnosticReportRecord.category;

      switch (diagnosticCategory) {
        case 'laboratory':
          canCreate = flags.isLabReportCreateDiagnosticReportEnabled;
          canEdit = flags.isLabReportUpdateDiagnosticReportEnabled;
          canDelete = flags.isLabReportDeleteDiagnosticReportEnabled;
          break;
        case 'imaging-and-tests':
          canCreate = flags.isImagingTestCreateDiagnosticReportEnabled;
          canEdit = flags.isImagingTestUpdateDiagnosticReportEnabled;
          canDelete = flags.isImagingTestDeleteDiagnosticReportEnabled;
          break;
      }
      break;
    case 'Encounter':
      const encounterRecord = record as EncounterRecord;
      const encounterCategory = encounterRecord.type;
      switch (encounterCategory) {
        case 'emergency':
          canCreate = flags.isEmergencyVisitCreateEncounterEnabled;
          canEdit = flags.isEmergencyVisitUpdateEncounterEnabled;
          canDelete = flags.isEmergencyVisitDeleteEncounterEnabled;
          break;
        case 'hospitalization':
          canCreate = flags.isHospitalizationCreateEncounterEnabled;
          canEdit = flags.isHospitalizationUpdateEncounterEnabled;
          canDelete = flags.isHospitalizationDeleteEncounterEnabled;
          break;
      }
      break;
    case 'Immunization':
      canCreate = flags.isImmunizationCreateImmunizationEnabled;
      canEdit = flags.isImmunizationUpdateImmunizationEnabled;
      canDelete = flags.isImmunizationDeleteImmunizationEnabled;
      break;
    case 'MedicationStatement':
      canCreate = flags.isMedicationCreateMedicationStatementEnabled;
      canEdit = flags.isMedicationUpdateMedicationStatementEnabled;
      canDelete = flags.isMedicationDeleteMedicationStatementEnabled;
      break;
    case 'Procedure':
      canCreate = flags.isProcedureCreateProcedureEnabled;
      canEdit = flags.isProcedureUpdateProcedureEnabled;
      canDelete = flags.isProcedureDeleteProcedureEnabled;
      break;
    case 'Observation':
      const observationRecord = record as ObservationRecord;
      const observationCategory = observationRecord.category;

      switch (observationCategory) {
        case 'body':
          canCreate = flags.isBodyCreateObservationEnabled;
          canEdit = flags.isBodyUpdateObservationEnabled;
          canDelete = flags.isBodyDeleteObservationEnabled;
          break;
        case 'genetic-information':
          canCreate = flags.isGeneticCreateObservationEnabled;
          canEdit = flags.isGeneticUpdateObservationEnabled;
          canDelete = flags.isGeneticDeleteObservationEnabled;
          break;
        case 'vital-signs':
          canCreate = flags.isVitalSignCreateObservationEnabled;
          canEdit = flags.isVitalSignUpdateObservationEnabled;
          canDelete = flags.isVitalSignDeleteObservationEnabled;
          break;
      }
      break;
  }

  return {
    canCreate,
    canDelete,
    canEdit,
  };
}
